import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bot, Settings, ArrowRight, Loader2, Eye, BarChart3, UserPlus } from "lucide-react";
import ClassInsightsModal from "@/components/ClassInsightsModal";
import DistrictAdminInviteModal from "@/components/DistrictAdminInviteModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Compact "LemonBot Activity" card for the educator dashboard. Shows
 * aggregated usage across ALL classes the educator owns + a quick link
 * into per-class settings to see the full breakdown.
 */
function EducatorLemonBotCard({ classes }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    total_messages: 0,
    active_students: 0,
    top_intent: null,
    classes_on: 0,
    classes_off: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [showDistrictModal, setShowDistrictModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classes || classes.length === 0) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const results = await Promise.all(
          classes.map(async (cls) => {
            try {
              const [usage, settings] = await Promise.all([
                axios.get(
                  `${API}/educator/lemonbot/usage?class_id=${cls.id}`,
                  authHeaders()
                ),
                axios.get(
                  `${API}/educator/lemonbot/settings/${cls.id}`,
                  authHeaders()
                ),
              ]);
              return { usage: usage.data, settings: settings.data };
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        const valid = results.filter(Boolean);
        const totalMessages = valid.reduce(
          (sum, r) => sum + (r.usage?.total_messages || 0),
          0
        );
        const allStudents = new Set();
        const intentTotals = {};
        let on = 0;
        let off = 0;
        valid.forEach((r) => {
          (r.usage?.by_student || []).forEach((s) => allStudents.add(s.user_id));
          (r.usage?.by_intent || []).forEach((i) => {
            intentTotals[i.intent] = (intentTotals[i.intent] || 0) + i.messages;
          });
          if (r.settings?.effective_enabled) on++;
          else off++;
        });
        const topIntent =
          Object.entries(intentTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
        setSummary({
          total_messages: totalMessages,
          active_students: allStudents.size,
          top_intent: topIntent,
          classes_on: on,
          classes_off: off,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classes]);

  // Where to send "Manage" — first class the educator owns. If none, hide CTA.
  const firstClassId = classes?.[0]?.id;

  return (
    <div
      className="bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-5 mb-6"
      data-testid="educator-lemonbot-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shadow-md flex-shrink-0">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              LemonBot Activity
            </h3>
            <p className="text-xs text-gray-600">
              AI Coach usage across all your classes
            </p>
          </div>
        </div>
        {firstClassId && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setShowInsightsModal(true)}
              data-testid="educator-lemonbot-card-insights-btn"
              className="text-sm font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-white/60"
            >
              <BarChart3 className="w-4 h-4" />
              Class Insights
            </button>
            <button
              onClick={() => setShowDistrictModal(true)}
              data-testid="educator-lemonbot-card-district-btn"
              className="text-sm font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-white/60"
            >
              <UserPlus className="w-4 h-4" />
              District Admin
            </button>
            <button
              onClick={() => navigate("/educator/view-as-student")}
              data-testid="educator-lemonbot-card-view-as-btn"
              className="text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-white/60"
            >
              <Eye className="w-4 h-4" />
              View as Student
            </button>
            <button
              onClick={() => navigate("/educator/student-preview")}
              data-testid="educator-lemonbot-card-preview-btn"
              className="text-sm font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-white/60"
            >
              <Eye className="w-4 h-4" />
              LemonBot Preview
            </button>
            <button
              onClick={() => navigate(`/class/${firstClassId}/settings`)}
              data-testid="educator-lemonbot-card-manage-btn"
              className="text-sm font-semibold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-white/60"
            >
              <Settings className="w-4 h-4" />
              Manage <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-3 mt-3">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <div className="bg-white rounded-lg border border-amber-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">
              Messages
            </div>
            <div className="text-xl font-bold text-amber-700">
              {summary.total_messages}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-emerald-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">
              Students Using
            </div>
            <div className="text-xl font-bold text-emerald-700">
              {summary.active_students}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-purple-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">
              Top Intent
            </div>
            <div
              className="text-sm font-bold text-purple-700 capitalize truncate"
              title={summary.top_intent || ""}
            >
              {summary.top_intent
                ? summary.top_intent.replace(/_/g, " ")
                : "—"}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">
              Classes On / Off
            </div>
            <div className="text-sm font-bold text-gray-800">
              <span className="text-emerald-600">{summary.classes_on}</span>
              {" / "}
              <span className="text-gray-500">{summary.classes_off}</span>
            </div>
          </div>
        </div>
      )}
      {showInsightsModal && (
        <ClassInsightsModal classes={classes} onClose={() => setShowInsightsModal(false)} />
      )}
      {showDistrictModal && (
        <DistrictAdminInviteModal onClose={() => setShowDistrictModal(false)} />
      )}
    </div>
  );
}

export default EducatorLemonBotCard;
